import { useContext } from 'react'
import { ProductContext } from 'src/components/restructure/product/contexts'
import {
  getProgressiveDiscountPricing,
  hasAppliedProgressiveDiscount,
  isListPriceHigherThan,
} from 'src/components/restructure/product/utils/progressiveDiscountPricing'
import { formatterPrice } from 'src/utils/formatterPrice'
import { Statsig } from 'src/sdk/statsig'
import { PROGRESSIVE_DISCOUNT_EXPERIMENT_ID } from 'src/components/common/StatsigExposure/StatsigExposure'
import type { SellerDefault } from 'src/components/restructure/product/types/productItem'

import { Installment } from './Installments'

type SellerOffer = SellerDefault['sellerOffer']

type DiscountPresentation = {
  amount: string | null
  percentage: number
}

function resolveDisplayedPrice(
  sellerOffer: SellerOffer,
  progressiveUnitPrice: number | null,
  hasActiveProgressiveDiscount: boolean
) {
  if (hasActiveProgressiveDiscount && progressiveUnitPrice !== null) {
    return progressiveUnitPrice / 100
  }

  if (sellerOffer.cashDiscountPercentage) {
    return sellerOffer.cashPrice
  }

  return sellerOffer.price
}

function resolveDiscountPresentation(
  sellerOffer: SellerOffer,
  progressivePercentage: number,
  hasActiveProgressiveDiscount: boolean
): DiscountPresentation | null {
  if (hasActiveProgressiveDiscount) {
    return {
      amount: null,
      percentage: progressivePercentage,
    }
  }

  if (!sellerOffer.hasDiscount && !sellerOffer.cashDiscountPercentage) {
    return null
  }

  const amount = sellerOffer.hasDiscount
    ? formatterPrice(sellerOffer.discount).replace(/\s/g, '')
    : null

  return {
    amount,
    percentage: sellerOffer.cashDiscountPercentage,
  }
}

function resolveDiscountBadgeLabel(discount: DiscountPresentation | null) {
  if (!discount) {
    return null
  }

  if (discount.amount) {
    return discount.amount
  }

  if (discount.percentage > 0) {
    return `${discount.percentage}% OFF`
  }

  return null
}

export function ProductPricing() {
  const isProgressiveDiscountEnabled = Statsig.useStatsigFeatureGateValue(
    PROGRESSIVE_DISCOUNT_EXPERIMENT_ID
  )

  const { currentSku, progressiveDiscount, quantity } =
    useContext(ProductContext)

  const {
    sellerDefault: { sellerOffer },
  } = currentSku

  const progressivePricing = getProgressiveDiscountPricing({
    progressiveDiscount,
    itemId: currentSku.itemId,
    quantity,
  })

  const hasActiveProgressiveDiscount =
    !!isProgressiveDiscountEnabled &&
    hasAppliedProgressiveDiscount(progressivePricing)

  const displayedPriceValue = resolveDisplayedPrice(
    sellerOffer,
    progressivePricing.discountedUnitPrice,
    hasActiveProgressiveDiscount
  )

  const currentPrice = formatterPrice(displayedPriceValue)
  const showPreviousPrice =
    sellerOffer.hasDiscount &&
    isListPriceHigherThan(sellerOffer.listPrice, displayedPriceValue)

  const discountPresentation = resolveDiscountPresentation(
    sellerOffer,
    progressivePricing.discountPercentage,
    hasActiveProgressiveDiscount
  )

  const discountBadgeLabel = showPreviousPrice
    ? resolveDiscountBadgeLabel(discountPresentation)
    : null

  const shouldShowCashDiscountTag =
    !!sellerOffer?.cashDiscountPercentage && !hasActiveProgressiveDiscount

  return (
    <div className="flex flex-col gap-1 restructure-small-desktop:gap-2">
      {currentSku.isAvailable ? (
        <>
          <div className="flex items-center mt-lg">
            <div className="flex flex-col">
              {showPreviousPrice && (
                <p className="text-sm leading-[28px] font-medium text-tertiary line-through restructure-small-desktop:leading-[21px] restructure-small-desktop:font-normal">
                  {formatterPrice(sellerOffer.listPrice)}
                </p>
              )}
              <span className="desktop-heading-title5 mb-3 text-restructure-primary flex items-center gap-2 font-['Inter', sans-serif] text-[20px] font-semibold leading-[100%]">
                {currentPrice}
                {shouldShowCashDiscountTag && (
                  <p className="text-sm text-secondary !font-normal">
                    à vista{' '}
                    <span className="text-green6">
                      ({sellerOffer.cashDiscountPercentage}% OFF)
                    </span>
                  </p>
                )}
                {discountBadgeLabel && (
                  <p className="w-fit bg-redDiscount text-xs leading-[18px] font-medium text-[#ffffff] p-1.5 restructure-small-desktop:text-sm restructure-small-desktop:leading-[21px]">
                    -{discountBadgeLabel}
                  </p>
                )}
              </span>
              <Installment />
            </div>
          </div>
        </>
      ) : (
        <h2 className="desktop-heading-title5 text-restructure-primary flex items-center gap-[24px] font-['Inter', sans-serif] text-[20px] py-[24px] font-semibold leading-[100%]">
          Produto indisponível
        </h2>
      )}
    </div>
  )
}
